import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Users, UserCheck, UserX, ShieldCheck, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import { BottomSheet } from "../referee/TacticalKit";
import { api, errMsg, label, fmtDate } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { PageHeader, Panel, MatchRow, Empty, Spinner, Avatar, Table, Crest, StatCard, IconBox } from "../../components/kit";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { TeamLogoCard } from "../../components/TeamLogoCard";

const useClub = () => {
  const { me } = useAuth();
  return me.memberships.find((m) => m.role === "club_admin" && m.status === "active")?.org_id;
};
const CATS = ["First Team", "Reserve Team", "Women's Team", "U23", "U20", "U17", "U15", "U13", "Custom"];

export function ClubTeams() {
  const cid = useClub();
  const [club, setClub] = useState(null);
  const [teams, setTeams] = useState([]);
  const [f, setF] = useState({ name: "", category: "U20" });
  const load = () => { api.get(`/clubs/${cid}`).then((r) => setClub(r.data)); api.get("/teams", { params: { club_id: cid } }).then((r) => setTeams(r.data)); };
  useEffect(() => { load(); }, []); // eslint-disable-line
  if (!club) return <Spinner />;
  return (
    <div className="space-y-6">
      <PageHeader title="Teams" sub={club.name} />
      <div className="flex items-center gap-4 tro-card p-4"><Crest club={club} size={64} /><div><div className="text-2xl font-extrabold">{club.name}</div><div className="text-sm text-[#6C757D]">{club.stadium} · Founded {club.founded}</div></div></div>
      <Panel title="Register team" testid="create-team-form">
        <div className="grid gap-2 md:grid-cols-3">
          <Input data-testid="team-name-input" placeholder="Team name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <select data-testid="team-category-select" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="h-12 rounded-xl border px-3 text-sm">{CATS.map((c) => <option key={c}>{c}</option>)}</select>
          <Button data-testid="create-team-btn" onClick={async () => { try { await api.post("/teams", { club_id: cid, ...f }); toast.success("Team created"); setF({ ...f, name: "" }); load(); } catch (e) { toast.error(errMsg(e)); } }}>Create team</Button>
        </div>
      </Panel>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="teams-grid">{teams.map((t) => <TeamLogoCard key={t.id} team={t} onChange={(updated) => setTeams((rows) => rows.map((row) => row.id === updated.id ? updated : row))} />)}</div>
    </div>
  );
}

function PlayerForm({ cid, teams, onDone }) {
  const [f, setF] = useState({ first_name: "", last_name: "", number: 20, position: "MF", team_id: teams[0]?.id || "", dob: "", registration_status: "registered", medical_notes: "" });
  const file = useRef(null);
  const submit = async () => {
    try {
      const { data } = await api.post("/players", { ...f, club_id: cid, number: +f.number, dob: f.dob || null });
      if (file.current?.files?.[0]) {
        const fd = new FormData(); fd.append("file", file.current.files[0]);
        await api.post(`/players/${data.id}/photo`, fd).catch((e) => toast.error(`Photo: ${errMsg(e)}`));
      }
      toast.success("Player created"); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const I = (k, p) => <Input data-testid={`player-${k}-input`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} {...p} />;
  return (
    <div className="grid gap-2 sm:grid-cols-2" data-testid="player-form">
      {I("first_name", { placeholder: "First name" })}{I("last_name", { placeholder: "Last name" })}
      {I("number", { type: "number", placeholder: "Shirt number" })}
      <select data-testid="player-position-select" value={f.position} onChange={(e) => setF({ ...f, position: e.target.value })} className="h-12 rounded-xl border px-3 text-sm">{["GK", "RB", "CB", "LB", "DM", "CM", "AM", "RW", "LW", "ST", "MF", "FW", "DF"].map((p) => <option key={p}>{p}</option>)}</select>
      <select data-testid="player-team-select" value={f.team_id} onChange={(e) => setF({ ...f, team_id: e.target.value })} className="h-12 rounded-xl border px-3 text-sm">{teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
      {I("dob", { type: "date" })}
      <select data-testid="player-status-select" value={f.registration_status} onChange={(e) => setF({ ...f, registration_status: e.target.value })} className="h-12 rounded-xl border px-3 text-sm">{["registered", "pending", "expired"].map((p) => <option key={p}>{p}</option>)}</select>
      <input ref={file} type="file" accept="image/*" data-testid="player-photo-input" className="text-sm" />
      {I("medical_notes", { placeholder: "Confidential medical notes (club only)" })}
      <Button data-testid="player-submit-btn" className="sm:col-span-2" onClick={submit}>Save player</Button>
    </div>
  );
}

function RemovePlayer({ p, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.delete(`/players/${p.id}`); toast.success(`${p.first_name} ${p.last_name} removed`); setOpen(false); onDone(); } catch (e) { toast.error(errMsg(e)); setOpen(false); }
    setBusy(false);
  };
  return (
    <>
      <Button variant="outline" className="h-12 w-full rounded-[14px] border-[#F02030]/40 text-[#F02030] hover:bg-[#F02030]/5" data-testid="remove-player-btn" onClick={() => setOpen(true)}><Trash2 className="h-4 w-4" />Remove player profile</Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="remove-player-dialog" className="w-[calc(100%-32px)] rounded-[20px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {p.first_name} {p.last_name}?</AlertDialogTitle>
            <AlertDialogDescription>Use this for a profile created by mistake. The profile is permanently deleted. Players who already appear on team sheets, match events or disciplinary records cannot be removed; set their registration to expired instead.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-player-cancel">Keep player</AlertDialogCancel>
            <AlertDialogAction data-testid="remove-player-confirm" disabled={busy} className="bg-[#F02030] hover:bg-[#d01a28]" onClick={(e) => { e.preventDefault(); go(); }}>{busy ? "Removing…" : "Remove permanently"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

const AVAIL = (p) => (p.suspended ? ["Suspended", "bg-[#F02030] text-white"] : p.registration_status !== "registered" ? [label(p.registration_status), "bg-[#FFD400] text-[#171B20]"] : ["Available", "bg-[#35865C] text-white"]);

const PlayerCard = ({ p, team, onOpen }) => {
  const [a, cls] = AVAIL(p);
  return (
    <button data-testid={`player-row-${p.number}-${p.last_name}`} onClick={onOpen} className="tro-card flex min-h-[72px] items-center gap-3 p-2.5 text-left transition-colors hover:border-[#171B20]">
      <Avatar player={p} size={52} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5"><span className="font-mono text-sm font-extrabold">#{p.number}</span><b className="truncate text-sm">{p.first_name} {p.last_name}</b>{p.is_minor && <span className="rounded bg-[#E7EAEE] px-1 text-[10px] font-bold">MINOR</span>}</div>
        <div className="truncate text-xs text-[#6C757D]">{p.position} · {team || "—"}</div>
      </div>
      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase ${cls}`}>{a}</span>
    </button>
  );
};

export function ClubPlayers() {
  const cid = useClub();
  const [players, setPlayers] = useState(null);
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const load = () => api.get("/players", { params: { club_id: cid } }).then((r) => setPlayers(r.data));
  useEffect(() => { load(); api.get("/teams", { params: { club_id: cid } }).then((r) => setTeams(r.data)); }, []); // eslint-disable-line
  if (!players) return <Spinner />;
  const tn = (id) => teams.find((t) => t.id === id)?.name;
  const rows = players.filter((p) => (!filter || p.team_id === filter) && (!q || `${p.first_name} ${p.last_name} ${p.number}`.toLowerCase().includes(q.toLowerCase()))).sort((a, b) => a.number - b.number);
  const stats = [["Registered", players.filter((p) => p.registration_status === "registered").length, Users], ["Available", players.filter((p) => !p.suspended && p.registration_status === "registered").length, UserCheck], ["Suspended", players.filter((p) => p.suspended).length, UserX], ["Minors", players.filter((p) => p.is_minor).length, ShieldCheck]];
  return (
    <div className="space-y-4" data-testid="club-teams-players">
      <PageHeader title="Teams & Players" sub={`${players.length} player profiles · minors' data is protected`}>
        <Button className="h-11 rounded-[14px]" data-testid="add-player-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4" />Add player</Button>
      </PageHeader>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">{stats.map(([l, v, I]) => (
        <div key={l} className="tro-card flex items-center gap-3 p-3" data-testid={`club-stat-${l.toLowerCase()}`}><IconBox icon={I} size="sm" /><div><div className="text-xl font-extrabold leading-none">{v}</div><div className="mt-1 text-[10px] font-bold uppercase tracking-wider text-[#6C757D]">{l}</div></div></div>
      ))}</div>
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="players-team-filter">
        {[{ id: "", name: "All teams" }, ...teams].map((t) => (
          <button key={t.id || "all"} data-testid={`team-chip-${t.id || "all"}`} onClick={() => setFilter(t.id)} className={`flex min-h-[44px] shrink-0 items-center gap-2 rounded-[14px] px-3 text-xs font-extrabold ${filter === t.id ? "bg-[#171B20] text-white" : "border border-[#E7EAEE] bg-white"}`}>
            {t.id && <Crest club={t} size={22} />}{t.name}<span className="opacity-60">{t.id ? players.filter((p) => p.team_id === t.id).length : players.length}</span>
          </button>
        ))}
        <Link to="/club/teams" className="flex min-h-[44px] shrink-0 items-center gap-1 rounded-[14px] border border-dashed border-[#C5CBD2] px-3 text-xs font-bold" data-testid="manage-teams-link"><Plus className="h-3.5 w-3.5" />Manage teams</Link>
      </div>
      <Input data-testid="players-search" className="h-12" placeholder="Search by name or shirt number" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 2xl:grid-cols-3" data-testid="players-table">{rows.map((p) => <PlayerCard key={p.id} p={p} team={tn(p.team_id)} onOpen={() => api.get(`/players/${p.id}`).then((r) => setDetail(r.data))} />)}</div>
      {!rows.length && <Empty text="No players match this view." />}
      <BottomSheet open={open} onClose={() => setOpen(false)} title="New player profile" desc="Registration, photo and confidential medical notes" testid="new-player-sheet">{teams.length > 0 && <PlayerForm cid={cid} teams={teams} onDone={() => { setOpen(false); load(); }} />}</BottomSheet>
      <BottomSheet open={!!detail} onClose={() => setDetail(null)} title={`${detail?.first_name || ""} ${detail?.last_name || ""}`} testid="player-detail">
        {detail && <div className="space-y-3 text-sm">
          <div className="flex items-center gap-3"><Avatar player={detail} size={84} /><div><div className="font-mono text-2xl font-extrabold">#{detail.number}</div><div>{detail.position} · {tn(detail.team_id)}</div><div className="text-xs text-[#6C757D]">DOB {detail.dob || "—"} · {label(detail.registration_status)}</div></div></div>
          <div className="grid grid-cols-4 gap-2 text-center">{[["Apps", detail.stats.appearances], ["Goals", detail.stats.goals], ["Yellow", detail.stats.yellow], ["Red", detail.stats.red]].map(([l, v]) => <div key={l} className="rounded-[14px] bg-[#F7F8FA] p-2"><div className="text-lg font-extrabold">{v}</div><div className="tro-label">{l}</div></div>)}</div>
          <div className="rounded-[14px] bg-[#F7F8FA] p-3"><span className="tro-label">Confidential medical</span><div>{detail.medical_notes || "—"}</div></div>
          <div><span className="tro-label">Disciplinary history</span>{detail.discipline.length ? detail.discipline.map((d) => <div key={d.id}>{label(d.type)} · {label(d.status)}</div>) : <div>None</div>}</div>
          <RemovePlayer p={detail} onDone={() => { setDetail(null); load(); }} />
        </div>}
      </BottomSheet>
    </div>
  );
}

export function ClubStaff() {
  const cid = useClub();
  const [list, setList] = useState(null);
  const [f, setF] = useState({ name: "", role: "", email: "" });
  const load = () => api.get(`/clubs/${cid}/staff`).then((r) => setList(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line
  if (!list) return <Spinner />;
  return (
    <div className="space-y-4">
      <PageHeader title="Staff directory" sub="Coaches and club staff" />
      <Panel title="Add staff member"><div className="grid gap-2 md:grid-cols-4">
        <Input data-testid="staff-name-input" placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Input data-testid="staff-role-input" placeholder="Role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} />
        <Input placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Button data-testid="staff-submit-btn" onClick={async () => { try { await api.post(`/clubs/${cid}/staff`, f); load(); toast.success("Staff added"); } catch (e) { toast.error(errMsg(e)); } }}>Add</Button>
      </div></Panel>
      <Table testid="staff-table" rows={list} cols={[["Name", (s) => <b>{s.name}</b>], ["Role", (s) => s.role], ["Email", (s) => s.email || "—"], ["Account", (s) => (s.platform_user ? "TRO user" : "Directory")]]} />
    </div>
  );
}

export function ClubFixtures() {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/matches", { params: { scope: "mine" } }).then((r) => setList(r.data)); }, []);
  if (!list) return <Spinner />;
  return <div><PageHeader title="Fixtures & Results" /><div className="space-y-2">{list.length ? list.map((m) => <MatchRow key={m.id} m={m} />) : <Empty />}</div></div>;
}

export function ClubFinances() {
  const cid = useClub();
  const [d, setD] = useState(null);
  const [f, setF] = useState({ kind: "expense", category: "", description: "", amount: "", date: new Date().toISOString().slice(0, 10) });
  const load = () => api.get(`/clubs/${cid}/finances`).then((r) => setD(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line
  if (!d) return <Spinner />;
  return (
    <div className="space-y-4">
      <PageHeader title="Finances" sub="Basic budget & expense records (club confidential)" />
      <div className="grid grid-cols-3 gap-3"><StatCard label="Income" value={d.income.toLocaleString()} testid="fin-income" /><StatCard label="Expenses" value={d.expense.toLocaleString()} /><StatCard label="Balance" value={d.balance.toLocaleString()} testid="fin-balance" /></div>
      <Panel title="New record"><div className="grid gap-2 md:grid-cols-6">
        <select data-testid="fin-kind" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className="h-12 rounded-xl border px-3 text-sm"><option>expense</option><option>income</option></select>
        <Input data-testid="fin-category" placeholder="Category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} />
        <Input placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        <Input data-testid="fin-amount" type="number" placeholder="Amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
        <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
        <Button data-testid="fin-submit" onClick={async () => { try { await api.post(`/clubs/${cid}/finances`, { ...f, amount: +f.amount }); load(); } catch (e) { toast.error(errMsg(e)); } }}>Add</Button>
      </div></Panel>
      <Table testid="finance-table" rows={d.rows} cols={[["Date", (r) => r.date], ["Type", (r) => label(r.kind)], ["Category", (r) => r.category], ["Description", (r) => r.description], ["Amount", (r) => <b className={r.kind === "expense" ? "text-[#F02030]" : ""}>{r.amount.toLocaleString()}</b>]]} />
    </div>
  );
}
export { fmtDate };
